import { getPlayerState, updatePlayerState } from '../common/channelPlayerState';

/**
 * Abstract base class for device-specific video players
 * Defines common interface that all device players must implement
 */
class BasePlayer {
  constructor() {
    if (new.target === BasePlayer) {
      throw new TypeError('Cannot construct BasePlayer instances directly');
    }
  }

  /**
   * Get current player state
   */
  getPlayerState() {
    return getPlayerState();
  }

  /**
   * Update player state
   */
  updatePlayerState(updates) {
    updatePlayerState(updates);
  }

  /**
   * Play media from URL
   * @param {string} url - Media URL to play
   * @param {boolean} isFullScreen - Whether to play in fullscreen
   * @returns {Promise<void>}
   */
  async play(url, isFullScreen = false) {
    throw new Error('play() must be implemented by subclass');
  }

  /**
   * Stop playback
   * @returns {Promise<void>}
   */
  async stop() {
    throw new Error('stop() must be implemented by subclass');
  }

  /**
   * Set display rectangle for video player
   * @param {Object} coords - { x, y, width, height } or { top, left, width, height }
   * @returns {Promise<void>}
   */
  async setDisplayRect(coords) {
    throw new Error('setDisplayRect() must be implemented by subclass');
  }

  /**
   * Toggle between fullscreen and windowed mode
   * @returns {Promise<void>}
   */
  async toggleFullscreen() {
    throw new Error('toggleFullscreen() must be implemented by subclass');
  }

  /**
   * Check if device supports fullscreen toggle
   * @returns {boolean}
   */
  supportsFullscreenToggle() {
    return false;
  }

  /**
   * Utility: delay execution
   * @param {number} ms - Milliseconds to delay
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Release native decoder between channel zaps without tearing down layout UI state.
   * Subclasses (TEP) override to avoid clearing preview-hole flags.
   */
  async resetForNextChannel() {
    if (typeof this.stop === 'function') {
      await this.stop();
    }
    this.updatePlayerState({ isPlayerBusy: false });
  }
}

export default BasePlayer;

